import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  SELLER_AGREEMENT_SECTION_KEYS,
  SELLER_AGREEMENT_VERSION,
} from "@/lib/sellerAgreement";

interface SellerAgreementGateProps {
  /** Called after the acceptance has been recorded successfully. */
  onAccepted?: () => void;
}

export function SellerAgreementGate({ onAccepted }: SellerAgreementGateProps) {
  const { t } = useTranslation("pages");
  const [accepted, setAccepted] = useState(false);
  const queryClient = useQueryClient();

  const accept = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("accept_seller_agreement", {
        p_version: SELLER_AGREEMENT_VERSION,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["seller", "onboarding"] });
      toast({
        title: t("sellerAgreement.gate.successTitle"),
        description: t("sellerAgreement.gate.successBody"),
      });
      onAccepted?.();
    },
    onError: (e: any) => {
      toast({
        title: t("sellerAgreement.gate.errorTitle"),
        description: e?.message ?? t("sellerAgreement.gate.errorBody"),
        variant: "destructive",
      });
    },
  });

  return (
    <Card className="perlla-shadow-soft max-w-3xl mx-auto" role="region" aria-live="polite">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {t("sellerAgreement.gate.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-muted-foreground">{t("sellerAgreement.gate.intro")}</p>

        <ScrollArea className="h-72 rounded-md border border-border p-4">
          <div className="space-y-5 pr-2">
            <p className="text-sm text-muted-foreground">
              {t("sellerAgreement.placeholderNotice")}
            </p>
            {SELLER_AGREEMENT_SECTION_KEYS.map((key, index) => (
              <div key={key} className="space-y-2">
                <h3 className="font-semibold text-foreground">
                  {index + 1}. {t(`sellerAgreement.sections.${key}.title`)}
                </h3>
                {t(`sellerAgreement.sections.${key}.body`)
                  .split("\n\n")
                  .map((paragraph, i) => (
                    <p key={i} className="text-sm text-muted-foreground leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
              </div>
            ))}
          </div>
        </ScrollArea>

        <a
          href="/seller-agreement"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          {t("sellerAgreement.gate.openFullPage")}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>

        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4">
          <Checkbox
            id="seller-agreement-gate-accept"
            checked={accepted}
            onCheckedChange={(v) => setAccepted(v === true)}
            className="mt-0.5"
          />
          <Label
            htmlFor="seller-agreement-gate-accept"
            className="text-sm font-normal leading-relaxed cursor-pointer"
          >
            {t("sellerAgreement.gate.checkbox")}
          </Label>
        </div>

        <Button
          variant="terracotta"
          size="lg"
          disabled={!accepted || accept.isPending}
          onClick={() => accept.mutate()}
        >
          {accept.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t("sellerAgreement.gate.submit")}
        </Button>
      </CardContent>
    </Card>
  );
}
